import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import WhatsAppTemplatePicker from "@/components/WhatsAppTemplatePicker";

const SNOOZE_KEY = "meda_wa_nudge_snooze";
const SNOOZE_MS = 2 * 60 * 60 * 1000; // 2 hours

const PILL = {
  Hot: "bg-rose-100 text-rose-700",
  Warm: "bg-amber-100 text-amber-700",
  Cool: "bg-sky-100 text-sky-700",
  Cold: "bg-slate-100 text-slate-600",
};

export default function WaDailyNudgeModal() {
  const { user } = useAuth();
  const [pending, setPending] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [open, setOpen] = useState(false);

  const snoozed = () => {
    const v = Number(localStorage.getItem(SNOOZE_KEY) || 0);
    return v && Date.now() < v;
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/wa-daily-pending");
      setPending(data.leads || []);
      setLoaded(true);
      if ((data.leads || []).length > 0 && !snoozed()) setOpen(true);
      if ((data.leads || []).length === 0) setOpen(false);
    } catch {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    load();
    const id = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [user, load]);

  const snooze = () => {
    localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_MS));
    setOpen(false);
  };

  if (!loaded || pending.length === 0) return null;

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent
        className="max-w-md"
        hideClose
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        data-testid="wa-daily-nudge-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#0A2540]">
            <AlertTriangle className="h-5 w-5 text-amber-500" /> Daily WhatsApp follow-ups
          </DialogTitle>
          <DialogDescription>
            Send today's WhatsApp template to each open lead. {pending.length} lead(s) still pending.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-72 space-y-1.5 overflow-y-auto">
          {pending.map((l) => (
            <div
              key={l.id}
              className="flex items-center gap-2 rounded-lg border border-slate-100 bg-slate-50/60 px-2.5 py-2"
              data-testid={`wa-nudge-lead-${l.id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-[#0A2540]">{l.name}</div>
                <div className="flex items-center gap-1.5 text-[11px] text-slate-500">
                  <span>{l.stage}</span>
                  {l.temperature && (
                    <span className={`rounded px-1 ${PILL[l.temperature] || "bg-slate-100 text-slate-600"}`}>
                      {l.temperature}
                    </span>
                  )}
                </div>
              </div>
              <WhatsAppTemplatePicker
                lead={l}
                baseBtn="relative inline-flex h-9 w-9 items-center justify-center rounded-full transition-colors"
                onSent={load}
                onChanged={load}
              />
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between pt-1">
          <span className="text-[11px] text-slate-400">Tap the green icon to send</span>
          <Button variant="ghost" size="sm" onClick={snooze} className="text-slate-500" data-testid="wa-nudge-snooze">
            Remind me in 2h
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
